import { useCallback, useEffect, useRef, useState } from 'react'

/** Chromium's install prompt event, which lib.dom does not declare. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/**
 * How long the app has to have been out of sight before returning to it is
 * treated as coming back later rather than glancing away. Reloading someone
 * who tabbed out for three seconds is worse than showing them yesterday's
 * build for another minute.
 */
const AWAY_BEFORE_AUTO_MS = 60_000

/** Long-lived tabs never fire visibilitychange, so ask on a timer as well. */
const POLL_MS = 30 * 60_000

export interface Pwa {
  /** A new build is installed and waiting for the page to hand over. */
  updateReady: boolean
  /** Activate the waiting worker and reload onto the new build. */
  applyUpdate: () => void
  /**
   * Take a waiting update now if the screen can afford to be pulled away.
   * The caller decides what "afford" means; this only checks there is
   * something to take.
   */
  applyIfIdle: () => void
  /** The browser is willing to show an install prompt. */
  canInstall: boolean
  install: () => void
  offline: boolean
  /** Drop cached weeks and photos, e.g. on sign-out. */
  clearCachedData: () => void
  /**
   * Re-issue the session check so it lands in the offline cache. On a first
   * visit the app's own call races the worker taking control and usually
   * bypasses it, which would leave an offline launch stuck on the lock screen.
   */
  cacheSession: () => void
}

export function usePwa(): Pwa {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null)
  const [updateReady, setUpdateReady] = useState(false)
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [offline, setOffline] = useState(() => !navigator.onLine)

  /* ---- service worker ------------------------------------------------- */

  useEffect(() => {
    // In dev the worker would sit in front of Vite's HMR transport.
    if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return

    let cancelled = false

    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        if (cancelled) return
        setRegistration(reg)
        if (reg.waiting) setUpdateReady(true)

        reg.addEventListener('updatefound', () => {
          const incoming = reg.installing
          if (!incoming) return
          incoming.addEventListener('statechange', () => {
            // `controller` is null on the very first install; that is a fresh
            // visit, not an update, and should not nag the user to reload.
            if (incoming.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateReady(true)
            }
          })
        })
      })
      .catch(() => {
        // No offline support; the app still works online.
      })

    return () => {
      cancelled = true
    }
  }, [])

  /*
   * Look for a new build when the app comes back to the foreground, and on a
   * slow timer for tabs that are never hidden at all.
   *
   * `hiddenSince` is what separates "came back to it" from "glanced away",
   * and the caller uses that to decide whether to swap silently.
   */
  const hiddenSince = useRef<number | null>(null)
  /*
   * A counter, not a flag. A flag latches: if a return is ignored because a
   * dialog was open, setting the same `true` again on the next return is a
   * no-op in React, nothing re-renders, and the update never lands. Counting
   * gives every qualifying return a value of its own.
   */
  const [returns, setReturns] = useState(0)

  useEffect(() => {
    if (!registration) return
    const onVisibility = () => {
      if (document.hidden) {
        hiddenSince.current = Date.now()
        return
      }
      const away = hiddenSince.current === null ? 0 : Date.now() - hiddenSince.current
      hiddenSince.current = null
      if (away >= AWAY_BEFORE_AUTO_MS) setReturns((n) => n + 1)
      void registration.update().catch(() => {})
    }
    const poll = window.setInterval(() => {
      if (!document.hidden) void registration.update().catch(() => {})
    }, POLL_MS)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.clearInterval(poll)
    }
  }, [registration])

  /*
   * The backstop, for a worker that cannot update itself.
   *
   * Everything above assumes a new worker eventually reaches `waiting`. If its
   * install keeps failing -- one precache entry 404s, storage is full -- it
   * never does, and the page serves the same stale shell forever with no strip
   * to press. Nothing on the page can tell that apart from being up to date,
   * because both look like "no update available".
   *
   * /build.json says what the origin is serving now, and the worker says what
   * it is serving this page. If they disagree and no new worker has arrived to
   * fix it, the registration is the problem: drop it and reload, which falls
   * back to plain HTTP and self-heals on the next visit.
   *
   * Once per session, online only, and only after giving a normal update a
   * chance to land -- so it can never turn into a reload loop.
   */
  useEffect(() => {
    if (!registration || !('serviceWorker' in navigator)) return
    if (!navigator.onLine) return
    const KEY = 'fl_sw_recovered'
    try {
      if (sessionStorage.getItem(KEY)) return
    } catch {
      return
    }

    let cancelled = false
    const shellVersion = () =>
      new Promise<string | null>((resolve) => {
        const worker = navigator.serviceWorker.controller
        if (!worker) return resolve(null)
        const channel = new MessageChannel()
        const timer = window.setTimeout(() => resolve(null), 3000)
        channel.port1.onmessage = (e) => {
          window.clearTimeout(timer)
          resolve(typeof e.data === 'string' ? e.data : null)
        }
        worker.postMessage('VERSION', [channel.port2])
      })

    void (async () => {
      try {
        const [served, res] = await Promise.all([
          shellVersion(),
          fetch('/build.json', { cache: 'no-store' }),
        ])
        if (cancelled || !served || !res.ok) return
        const { build } = (await res.json()) as { build?: string }
        if (!build || build === served) return

        // Stale. Give the ordinary path its chance before doing anything blunt.
        await registration.update().catch(() => {})
        await new Promise((r) => window.setTimeout(r, 8000))
        if (cancelled || registration.waiting || navigator.serviceWorker.controller === null) return

        sessionStorage.setItem(KEY, '1')
        await registration.unregister().catch(() => {})
        window.location.reload()
      } catch {
        // Offline, blocked storage, or a worker that never answers: leave it.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [registration])

  const applyUpdate = useCallback(() => {
    const waiting = registration?.waiting
    if (!waiting) {
      window.location.reload()
      return
    }
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
      once: true,
    })
    waiting.postMessage('SKIP_WAITING')
  }, [registration])

  /*
   * Swap to the new build without being asked, but only after a real absence.
   * Returning from the shop to a page that reloads under you is startling;
   * coming back to the app tomorrow and finding it current is invisible.
   */
  const applyIfIdle = useCallback(() => {
    if (returns === 0 || !registration?.waiting) return
    setReturns(0)
    applyUpdate()
  }, [returns, registration, applyUpdate])

  const clearCachedData = useCallback(() => {
    navigator.serviceWorker?.controller?.postMessage('CLEAR_DATA')
  }, [])

  const cacheSession = useCallback(() => {
    if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return
    navigator.serviceWorker.ready
      .then(() => {
        if (!navigator.serviceWorker.controller) return
        return fetch('/api/auth/me', { credentials: 'same-origin' })
      })
      .catch(() => {
        // Best effort: without it the app simply needs a connection to start.
      })
  }, [])

  /* ---- install prompt -------------------------------------------------- */

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => setInstallPrompt(null)
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const install = useCallback(() => {
    if (!installPrompt) return
    void installPrompt.prompt().then(() => setInstallPrompt(null))
  }, [installPrompt])

  /* ---- connectivity ---------------------------------------------------- */

  useEffect(() => {
    const update = () => setOffline(!navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])

  return {
    updateReady,
    applyUpdate,
    applyIfIdle,
    canInstall: installPrompt !== null,
    install,
    offline,
    clearCachedData,
    cacheSession,
  }
}

/** Read the `?tab=` parameter used by the manifest's app shortcuts. */
/**
 * The week a link points at, as `?week=YYYY-MM-DD`. Notification emails link
 * straight to the week they are about, which is the whole reason to send one.
 */
export function initialWeekFromUrl(): string | null {
  try {
    const week = new URL(window.location.href).searchParams.get('week')
    return week && /^\d{4}-\d{2}-\d{2}$/.test(week) ? week : null
  } catch {
    return null
  }
}

export function initialTabFromUrl(valid: readonly string[]): string | null {
  try {
    const tab = new URL(window.location.href).searchParams.get('tab')
    return tab && valid.includes(tab) ? tab : null
  } catch {
    return null
  }
}
